<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { getApiErrorMessage } from '@/utils/apiError'

import userService from '../../services/userService'

definePage({
  meta: {
    title: 'pages.user.confirmEmail.title',
    requiresAuth: false,
  },
})

const loading = ref(true)
const errorMessage = ref('')
const successMessage = ref('')
const { t } = useI18n()

const router = useRouter()
const route = useRoute()

onMounted(async () => {
  const userId = route.query.userId?.toString()
  const token = route.query.token?.toString()
  const changedEmail = route.query.changedEmail?.toString()

  if (!userId || !token) {
    errorMessage.value = t('pages.user.confirmEmail.messages.invalidLink')
    loading.value = false
    return
  }

  try {
    const success = await userService.confirmEmail(userId, token, changedEmail)
    if (success) {
      successMessage.value = t('pages.user.confirmEmail.messages.success')
    } else {
      errorMessage.value = t('pages.user.confirmEmail.messages.genericError')
    }
  } catch (error: unknown) {
    console.error('Email confirmation error:', error)
    errorMessage.value = getApiErrorMessage(
      error,
      t,
      t('pages.user.confirmEmail.messages.genericError'),
    )
  } finally {
    loading.value = false
  }
})

const navigateToLogin = () => {
  router.push('/user/login')
}
</script>

<template>
  <div style="max-width: 400px" class="mycard px-4 py-2">
    <div class="mb-4 flex flex-row justify-between">
      <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-200">
        {{ t('pages.user.confirmEmail.title') }}
      </h1>
    </div>

    <div v-if="loading" class="flex flex-col items-center gap-3 py-6">
      <AppSpinner />
      <p>{{ t('pages.user.confirmEmail.loading') }}</p>
    </div>

    <div v-else>
      <AppAlert v-if="errorMessage" color="error" :closable="false" class="mb-4">
        {{ errorMessage }}
      </AppAlert>
      <AppAlert v-if="successMessage" color="success" :closable="false" class="mb-4">
        {{ successMessage }}
      </AppAlert>

      <div class="mt-5 flex flex-row justify-center">
        <UButton
          :label="t('pages.user.confirmEmail.actions.backToLogin')"
          @click="navigateToLogin"
        />
      </div>
    </div>
  </div>
</template>
